import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FaUsers, FaMoneyBillWave, FaEye, FaCheckDouble } from "react-icons/fa";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import { authFetch } from "../../utils/api/authFetch";
import { StatCard, Panel } from "./shell";

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;
const truncate = (s, n = 22) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "");

// Detailed analytics: how the teacher's content is moving. Trends over the last
// 6 months plus a top-content ranking, all from /api/marketplace/analytics.
const StudioAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await authFetch("/api/marketplace/analytics");
                const d = await res.json().catch(() => ({}));
                setData(d.data || null);
            } catch (err) {
                toast.error("Could not load analytics");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <p className="text-gray-500">Loading…</p>;

    const t = data?.totals || {};
    const trend = data?.trend || [];
    const top = (data?.topContent || []).filter((c) => c.opens > 0).map((c) => ({ ...c, label: truncate(c.title) }));
    const hasTrend = trend.some((p) => p.subscribers || p.opens || p.completions);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard icon={FaUsers} label="Subscribers" value={t.subscribers ?? 0} />
                <StatCard icon={FaMoneyBillWave} label="Monthly revenue" value={naira(t.monthlyRevenue)} accent="text-green-600" />
                <StatCard icon={FaEye} label="Total opens" value={t.totalOpens ?? 0} accent="text-indigo-600" />
                <StatCard icon={FaCheckDouble} label="Completions" value={t.totalCompletions ?? 0} accent="text-emerald-600" />
            </div>

            {!hasTrend && top.length === 0 ? (
                <Panel>
                    <p className="text-sm text-gray-400 py-12 text-center">
                        No activity yet. As students open your lessons and subscribe, your growth and top content appear here.
                    </p>
                </Panel>
            ) : (
                <>
                    <Panel title="New subscribers per month">
                        <div className="h-56 sm:h-64 -ml-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={trend} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} cursor={{ fill: "#f8fafc" }} />
                                    <Bar dataKey="subscribers" name="New subscribers" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={44} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Panel>

                    <Panel title="Lesson opens vs completions">
                        <div className="h-56 sm:h-64 -ml-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trend} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="opens" name="Opens" stroke="#6366f1" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="completions" name="Completions" stroke="#10b981" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Panel>

                    <Panel title="Top content by opens">
                        {top.length === 0 ? (
                            <p className="text-sm text-gray-400 py-6 text-center">No opens yet.</p>
                        ) : (
                            <div style={{ height: Math.max(top.length * 42, 120) }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={top} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} cursor={{ fill: "#f8fafc" }} />
                                        <Bar dataKey="opens" name="Opens" radius={[0, 6, 6, 0]} maxBarSize={22}>
                                            {top.map((_, i) => <Cell key={i} fill="#2563eb" />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Panel>
                </>
            )}
        </div>
    );
};

export default StudioAnalytics;
